import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, FileText, X, Loader2, CheckCircle2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import SiteLayout from "@/components/site/SiteLayout";
import Seo from "@/components/site/Seo";
import ParticleField from "@/components/site/ParticleField";
import { SectionTag, Reveal } from "@/components/site/Reveal";
import { fetchResources, downloadResource, toAbsolute } from "@/lib/api";

const L = {
  ar: {
    title: "مركز التحميلات",
    tag: "موارد مجانية",
    subtitle: "حمّل ملفنا التعريفي، دراسات الحالة، والدلائل التقنية المجانية — كلها في مكان واحد.",
    empty: "قريبًا سنضيف موارد قابلة للتحميل.",
    download: "تحميل",
    download_now: "تحميل مباشر",
    lead_title: "أدخل بريدك للتحميل",
    lead_desc: "سيصلك رابط التحميل فورًا، ولن نشاركه مع أي طرف ثالث.",
    name: "الاسم", email: "البريد الإلكتروني", phone: "الهاتف (اختياري)", company: "الشركة (اختياري)",
    submit: "تحميل الآن", sending: "جارٍ التحميل...", success: "شكرًا! جارٍ فتح الملف...",
    close: "إغلاق", pages: "صفحة",
    categories: { all: "الكل", profile: "الملف التعريفي", "case-study": "دراسات حالة", guide: "دلائل تقنية" },
  },
  en: {
    title: "Resources Hub", tag: "Free Resources",
    subtitle: "Download our company profile, case studies, and free tech guides — all in one place.",
    empty: "Downloadable resources coming soon.",
    download: "Download", download_now: "Instant download",
    lead_title: "Enter your email to download",
    lead_desc: "You'll get the download link instantly, we never share your data.",
    name: "Name", email: "Email", phone: "Phone (optional)", company: "Company (optional)",
    submit: "Download now", sending: "Downloading...", success: "Thanks! Opening file...",
    close: "Close", pages: "pages",
    categories: { all: "All", profile: "Profile", "case-study": "Case Studies", guide: "Guides" },
  },
  ku: {
    title: "ناوەندی داگرتنەکان", tag: "سەرچاوە بەخۆڕایی",
    subtitle: "دۆسیەی پێناسە، لێکۆڵینەوە و ڕێبەرە تەکنیکییەکانمان بەخۆڕایی داگرە.",
    empty: "بەم زووانە سەرچاوەکان زیاد دەکرێن.",
    download: "داگرتن", download_now: "داگرتنی خێرا",
    lead_title: "ئیمەیلەکەت بنووسە بۆ داگرتن",
    lead_desc: "دەستبەجێ لینکی داگرتن دەگاتە دەستت.",
    name: "ناو", email: "ئیمەیل", phone: "مۆبایل (ئیختیاری)", company: "کۆمپانیا (ئیختیاری)",
    submit: "داگرتن", sending: "لە داگرتندایە...", success: "سوپاس! ...",
    close: "داخستن", pages: "پەڕە",
    categories: { all: "هەموو", profile: "پێناسە", "case-study": "لێکۆڵینەوە", guide: "ڕێبەر" },
  },
};

function LeadModal({ resource, onClose, lang }) {
  const T = L[lang] || L.ar;
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "" });
  const [busy, setBusy] = useState(false);
  const inputCls = "w-full rounded-xl bg-[#0b1731] border border-white/10 focus:border-[color:var(--primary)] px-4 py-2.5 text-sm outline-none";

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || busy) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) { toast.error(T.email); return; }
    setBusy(true);
    try {
      const r = await downloadResource({ resource_id: resource.id, ...form, lang });
      toast.success(T.success);
      const url = r.data.file_url;
      if (url) window.open(toAbsolute(url), "_blank", "noopener");
      onClose();
    } catch { toast.error("Failed"); }
    finally { setBusy(false); }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[80] grid place-items-center bg-black/85 backdrop-blur-md p-4 overflow-y-auto"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="resource-lead-modal"
    >
      <motion.div
        className="relative w-full max-w-md rounded-3xl card-surface p-6 sm:p-8 my-8"
        initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose} className="absolute top-4 end-4 h-8 w-8 grid place-items-center rounded-full border border-white/10 hover:border-[color:var(--primary)] text-slate-400" data-testid="lead-modal-close"><X size={16} /></button>
        <form onSubmit={submit} className="space-y-3">
          <h3 className="text-xl font-extrabold mb-1">{T.lead_title}</h3>
          <div className="text-sm text-primary font-semibold mb-1">{resource.title}</div>
          <p className="text-xs text-slate-500 mb-2">{T.lead_desc}</p>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={T.name} className={inputCls} data-testid="lead-name" />
          <input type="email" dir="ltr" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={T.email + " *"} className={inputCls} data-testid="lead-email" required />
          <input dir="ltr" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={T.phone} className={inputCls} data-testid="lead-phone" />
          <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder={T.company} className={inputCls} data-testid="lead-company" />
          <button type="submit" disabled={busy} className="w-full rounded-full py-3 font-extrabold text-[#04120a] disabled:opacity-60 flex items-center justify-center gap-2" style={{ background: "var(--primary)" }} data-testid="lead-submit">
            {busy ? <><Loader2 size={16} className="animate-spin" /> {T.sending}</> : <><Download size={16} /> {T.submit}</>}
          </button>
        </form>
      </motion.div>
    </motion.div>
  );
}

const CATEGORY_ICONS = {
  profile: FileText,
  "case-study": CheckCircle2,
  guide: BookOpen,
};

export default function Resources() {
  const { lang } = useLang();
  const T = L[lang] || L.ar;
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [activeResource, setActiveResource] = useState(null);

  useEffect(() => { fetchResources(lang).then((r) => setItems(r.data || [])).catch(() => setItems([])); }, [lang]);

  const list = items || [];
  const cats = Array.from(new Set(list.map((r) => r.category).filter(Boolean)));
  const filtered = filter === "all" ? list : list.filter((r) => r.category === filter);

  const handleDownload = (res) => {
    if (res.requires_lead) setActiveResource(res);
    else if (res.file_url) window.open(toAbsolute(res.file_url), "_blank", "noopener");
  };

  return (
    <SiteLayout>
      <Seo title={T.title} description={T.subtitle} />
      <section className="relative overflow-hidden pt-16 pb-8" data-testid="resources-page">
        <ParticleField className="opacity-40" density={30} />
        <div className="absolute inset-0 radial-fade" />
        <div className="relative z-10 mx-auto max-w-5xl px-5 sm:px-8">
          <SectionTag>{T.tag}</SectionTag>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mt-5 mb-4">{T.title}</h1>
          <p className="text-slate-400 text-lg max-w-2xl">{T.subtitle}</p>
        </div>
      </section>

      <section className="section-pad pt-4">
        <div className="mx-auto max-w-6xl px-5 sm:px-8">
          {cats.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8" data-testid="resources-filters">
              {["all", ...cats].map((c) => (
                <button
                  key={c}
                  onClick={() => setFilter(c)}
                  className={"rounded-full px-4 py-1.5 text-xs font-bold border transition-colors " + (filter === c ? "text-[#04120a] border-transparent" : "border-white/10 text-slate-300 hover:border-[color:var(--primary)]")}
                  style={filter === c ? { background: "var(--primary)" } : {}}
                  data-testid={`resource-filter-${c}`}
                >
                  {T.categories[c] || c}
                </button>
              ))}
            </div>
          )}

          {items === null ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-primary" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-slate-500">{T.empty}</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="resources-grid">
              {filtered.map((r, i) => {
                const Icon = CATEGORY_ICONS[r.category] || FileText;
                return (
                  <Reveal key={r.id} delay={0.05 + i * 0.05}>
                    <div className="group card-surface rounded-3xl overflow-hidden h-full flex flex-col hover:-translate-y-1 transition-all duration-500" data-testid={`resource-${i}`}>
                      <div className="relative aspect-video bg-[#0b1731] overflow-hidden">
                        {r.cover ? (
                          <img src={toAbsolute(r.cover)} alt={r.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        ) : (
                          <div className="w-full h-full grid place-items-center" style={{ background: "radial-gradient(circle at 50% 50%, rgba(0,242,96,0.15), transparent)" }}>
                            <Icon size={56} className="text-primary/50" />
                          </div>
                        )}
                        {r.category && (
                          <span className="absolute top-3 start-3 rounded-full px-2.5 py-1 text-[10px] font-extrabold bg-black/70 border border-white/10 text-primary uppercase tracking-wider">
                            {T.categories[r.category] || r.category}
                          </span>
                        )}
                      </div>
                      <div className="p-5 flex-1 flex flex-col">
                        <h3 className="text-lg font-extrabold mb-2 line-clamp-2">{r.title}</h3>
                        {r.description && <p className="text-sm text-slate-400 leading-relaxed line-clamp-3 mb-3">{r.description}</p>}
                        <div className="mt-auto flex items-center justify-between gap-2 flex-wrap pt-3">
                          <div className="text-xs text-slate-500 flex items-center gap-3">
                            {r.pages > 0 && <span>{r.pages} {T.pages}</span>}
                            {r.file_size && <span dir="ltr">{r.file_size}</span>}
                          </div>
                          <button
                            onClick={() => handleDownload(r)}
                            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-extrabold text-[#04120a]"
                            style={{ background: "var(--primary)" }}
                            data-testid={`resource-download-${i}`}
                          >
                            <Download size={13} /> {r.requires_lead ? T.download : T.download_now}
                          </button>
                        </div>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <AnimatePresence>
        {activeResource && <LeadModal resource={activeResource} lang={lang} onClose={() => setActiveResource(null)} />}
      </AnimatePresence>
    </SiteLayout>
  );
}
